import React, { useState } from 'react';
import { db, auth } from './firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';

const S = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#0A0E1Aee', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#111827', border: '1px solid #1E3A5F', borderRadius: 12, padding: 24, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
  title: { fontSize: 18, fontWeight: 900, color: '#F8FAFC', marginBottom: 4 },
  sub: { fontSize: 12, color: '#9CA3AF', marginBottom: 20 },
  label: { fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6, display: 'block', marginTop: 14 },
  input: { background: '#1E3A5F22', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  textarea: { background: '#1E3A5F22', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box', minHeight: 100, resize: 'vertical' },
  select: { background: '#111827', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  row: { display: 'flex', gap: 12 },
  btn: (color) => ({ background: color || '#1E3A5F', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }),
  error: { color: '#D32F2F', fontSize: 12, marginTop: 8 },
  levelBtn: (selected, color) => ({ background: selected ? color + '33' : 'transparent', border: `2px solid ${selected ? color : '#1E3A5F44'}`, color: selected ? color : '#9CA3AF', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: selected ? 700 : 400, flex: 1 }),
};

const CATEGORIES = ['Theft', 'Trespassing', 'Suspicious Activity', 'Fire Safety', 'Health & Safety', 'Aggressive Behaviour', 'Security Threat', 'Medical Emergency', 'Criminal Damage', 'Anti-Social Behaviour', 'Access Control Breach', 'Other'];
const LEVELS = [
  { id: 'Level 1 Minor', label: 'L1 Minor', color: '#2E7D32' },
  { id: 'Level 2 Serious', label: 'L2 Serious', color: '#F57F17' },
  { id: 'Level 3 Emergency', label: 'L3 Emergency', color: '#D32F2F' },
];

export default function IncidentModal({ incident, tenantId, onClose, onSaved }) {
  const isEdit = !!incident;
  const [form, setForm] = useState({
    siteName: incident?.siteName || '',
    category: incident?.category || 'Theft',
    title: incident?.title || '',
    description: incident?.description || incident?.full_description || '',
    action_taken: incident?.action_taken || '',
    escalationLevel: incident?.escalationLevel || 'Level 1 Minor',
    police_reference_number: incident?.police_reference_number || '',
    emergency_services_called: incident?.emergency_services_called || false,
    supervisor_notified: incident?.supervisor_notified || false,
    status: incident?.status || 'open',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.siteName.trim() || !form.description.trim()) {
      setError('Site name and description are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const user = auth.currentUser;
      if (isEdit && incident._id) {
        await updateDoc(doc(db, 'incidents', incident._id), {
          ...form,
          last_edited_at: serverTimestamp(),
          last_edited_by: user?.email,
          version_number: (incident.version_number || 1) + 1,
          report_status: 'pending_review',
        });
      } else {
        const incId = `INC-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
        await addDoc(collection(db, 'incidents'), {
          ...form,
          id: incId,
          companyId: tenantId,
          company_id: tenantId,
          guardName: user?.email || 'Control Room',
          reported_by: user?.email || 'Control Room',
          createdAt: serverTimestamp(),
          status: 'open',
          report_status: 'submitted',
          version_number: 1,
          evidence_links: [],
          source: 'control_room',
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={S.title}>{isEdit ? 'Edit incident' : 'Create incident'}</div>
            <div style={S.sub}>{isEdit ? `Editing ${incident.id} — creates new version` : 'Log a new incident from control room'}</div>
          </div>
          <button onClick={onClose} style={{ ...S.btn('#D32F2F22'), color: '#D32F2F', padding: '6px 12px' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={S.row}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Site name *</label>
              <input style={S.input} placeholder="e.g. Paddington Square" value={form.siteName} onChange={e => set('siteName', e.target.value)} required />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Category</label>
              <select style={S.select} value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <label style={S.label}>Title</label>
          <input style={S.input} placeholder="Brief title" value={form.title} onChange={e => set('title', e.target.value)} />

          <label style={S.label}>Escalation level</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {LEVELS.map(l => (
              <button key={l.id} type="button" style={S.levelBtn(form.escalationLevel === l.id, l.color)}
                onClick={() => set('escalationLevel', l.id)}>{l.label}</button>
            ))}
          </div>

          <label style={S.label}>Full description *</label>
          <textarea style={S.textarea} placeholder="Describe the incident in full..." value={form.description} onChange={e => set('description', e.target.value)} required />

          <label style={S.label}>Action taken</label>
          <textarea style={S.textarea} placeholder="What action was taken..." value={form.action_taken} onChange={e => set('action_taken', e.target.value)} style={{ ...S.textarea, minHeight: 70 }} />

          <label style={S.label}>Police reference number</label>
          <input style={S.input} placeholder="e.g. CAD 1234/2026" value={form.police_reference_number} onChange={e => set('police_reference_number', e.target.value)} />

          <div style={{ display: 'flex', gap: 20, marginTop: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.emergency_services_called} onChange={e => set('emergency_services_called', e.target.checked)} />
              <span style={{ fontSize: 13, color: '#F8FAFC' }}>Emergency services called</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.supervisor_notified} onChange={e => set('supervisor_notified', e.target.checked)} />
              <span style={{ fontSize: 13, color: '#F8FAFC' }}>Supervisor notified</span>
            </label>
          </div>

          {isEdit && (
            <div style={{ marginTop: 14 }}>
              <label style={S.label}>Status</label>
              <select style={S.select} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="open">Open</option>
                <option value="under_review">Under review</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          )}

          {error && <div style={S.error}>{error}</div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ ...S.btn('#1E3A5F44'), flex: 1 }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ ...S.btn(isEdit ? '#F57F17' : '#2E7D32'), flex: 2 }}>
              {loading ? 'Saving…' : isEdit ? 'Save changes' : 'Create incident'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}